import { InstrumentIcon } from '@foundation/apis/payments';
import cn from 'clsx';

import { ChipIcon } from '@/components/common/icons/ChipIcon';
import { NFCIcon } from '@/components/common/icons/NFCIcon';
import { PaymentIcon } from '@/components/common/logos/PaymentIcon';

import './PaymentInstrumentCard.css';
import hashValue from '../../../../common/HashValue';

const NUMBER_OF_CARD_VARIANTS = 3;

interface IPaymentInstrumentCardProps {
  readonly iconName: InstrumentIcon;
  readonly maskedInfo: string;
  readonly small?: boolean;
  readonly expired?: boolean;
  readonly bgColor?: 'default' | 'white';
}

export function PaymentInstrumentCard(props: IPaymentInstrumentCardProps) {
  const { maskedInfo, small = false } = props;
  const variant = Math.max(
    hashValue(maskedInfo) % (NUMBER_OF_CARD_VARIANTS + 1),
    1,
  );

  if (small) {
    return (
      <PaymentInstrumentCardSmall
        {...props}
        variant={variant}
      />
    );
  }

  return (
    <PaymentInstrumentCardLarge
      {...props}
      variant={variant}
    />
  );
}

interface IPaymentInstrumentCardInternalProps {
  readonly iconName: InstrumentIcon;
  readonly variant: number;
  readonly expired?: boolean;
  readonly bgColor?: 'default' | 'white';
}

function PaymentInstrumentCardSmall(
  props: IPaymentInstrumentCardInternalProps,
) {
  const { iconName, variant, bgColor = 'default' } = props;

  return (
    <div
      className={cn(
        bgColor === 'default' ? 'pmi-payment-instrument-card' : 'border h-9',
        'rounded-md flex-none w-[64px] p-2 flex items-center justify-center text-center',
      )}
      role="presentation"
      aria-hidden="true"
      data-instrument-icon={iconName}
      data-instrument-variant={variant}
    >
      <PaymentIcon
        /*bgColor={bgColor}*/ icon={iconName}
        extraClassName="h-full"
      />
    </div>
  );
}

function PaymentInstrumentCardLarge(
  props: IPaymentInstrumentCardInternalProps,
) {
  const { iconName, variant, expired = false, bgColor = 'default' } = props;

  const isCard = [
    InstrumentIcon.Amex,
    InstrumentIcon.Diners,
    InstrumentIcon.Discover,
    InstrumentIcon.Elo,
    InstrumentIcon.GenericCard,
    InstrumentIcon.GooglePay,
    InstrumentIcon.Jcb,
    InstrumentIcon.Maestro,
    InstrumentIcon.MasterCard,
    InstrumentIcon.UnionPay,
    InstrumentIcon.Visa,
  ].includes(iconName);

  return (
    <div
      className={cn(
        bgColor === 'default' ? 'pmi-payment-instrument-card' : 'border h-9',
        'rounded-md flex-none w-full p-4 flex flex-col justify-between text-sm',
      )}
      data-instrument-icon={iconName}
      data-instrument-variant={expired ? 'expired' : variant}
    >
      {isCard ? (
        <>
          <div className="">
            <div className="flex justify-end">
              <NFCIcon className="w-5" />
            </div>
            <ChipIcon className="w-6 aspect-square" />
          </div>
          <div className="flex flex-row justify-between items-center w-full">
            <span aria-hidden="true">**** **** **** ****</span>
            <PaymentIcon
              icon={iconName}
              extraClassName="w-1/4 h-auto border-0"
              bgColor="transparent"
            />
          </div>
        </>
      ) : (
        <>
          <PaymentIcon
            icon={iconName}
            extraClassName="w-1/4 h-auto"
            bgColor="transparent"
          />
          <div className="text-right">
            <span aria-hidden="true">**** **** **** ****</span>
          </div>
        </>
      )}
    </div>
  );
}
